import React, { createContext, useMemo, useState } from "react";
import { FlagProvider } from "@unleash/proxy-client-react";
import { getFlagsClient } from "../singleton";

type Props = {
  /** For identification purposes to Unleash */
  applicationName: string;
  /**
   * Whether to actually fetch flags or not. There are some cases where we show a
   * "preview" version of the app, and this can be useful to avoid fetching
   * flags in those cases.
   *
   * If you find yourself using this, please reach out to the platform team
   * */
  shouldStartClient?: boolean;
  children?: React.ReactNode;
};

export interface ContextProps {
  arenaId: string;
  setArenaId: (id: string) => void;
}

export const ArenaContext = createContext<ContextProps | undefined>(undefined);

export function FeatureFlagProvider({
  children,
  applicationName,
  shouldStartClient = true,
}: Props) {
  const unleashClient = getFlagsClient(applicationName);
  const [arenaId, setArenaId] = useState<string>("");

  // if we could not resolve the client, we should not start it
  const shouldClientStart = useMemo(
    () => shouldStartClient && unleashClient !== undefined,
    [shouldStartClient, unleashClient],
  );

  const arenaProviderContext = useMemo(() => ({ arenaId, setArenaId }), [arenaId, setArenaId]);

  return (
    <FlagProvider unleashClient={unleashClient} startClient={shouldClientStart}>
      <ArenaContext.Provider value={arenaProviderContext}>{children}</ArenaContext.Provider>
    </FlagProvider>
  );
}
